import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * Single source of truth for phone handling on the client.
 *
 * The server (gmb-server/src/utils/phoneNumber.js) validates with
 * libphonenumber-js and stores E.164 — Twilio needs E.164 to send. Historically
 * the client used a loose regex that accepted "(415) 555-0132", which the server
 * then rejected with a 400. Every form should normalise through here before it
 * submits so the two sides always agree.
 */

// Numbers typed without a country code are assumed to be US/CA (NANP). An
// explicit "+<cc>" always wins, so international staff can still enter e.g.
// "+63 917 123 4567".
export const DEFAULT_COUNTRY = "US";

// A friendly example to show in placeholders / error copy. We accept far more
// than this (any format libphonenumber can parse) and normalise for the user.
export const PHONE_EXAMPLE = "(415) 555-0132";

/**
 * Normalise any reasonable phone input to E.164, or null if it isn't a valid
 * number.
 *
 *   "(415) 555-0132"      -> "+14155550132"
 *   "415-555-0132"        -> "+14155550132"
 *   "+63 917 123 4567"    -> "+639171234567"
 *   "not a phone" / "123" -> null
 */
export const toE164 = (raw) => {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return null;

  try {
    const parsed = parsePhoneNumberFromString(trimmed, DEFAULT_COUNTRY);
    return parsed && parsed.isValid() ? parsed.number : null;
  } catch {
    return null;
  }
};

export const isValidPhone = (raw) => toE164(raw) !== null;

/**
 * Yup transform: blank -> undefined (so `.required()` / `.notRequired()` behave),
 * a valid number -> its E.164 form (so that's what gets submitted), and an
 * invalid non-blank value is passed through untouched so the follow-on
 * `.test(isValidPhone)` can reject it with a readable message.
 */
export const yupPhoneTransform = (value) => {
  const trimmed = typeof value === "string" ? value.trim() : value;
  if (trimmed === "" || trimmed == null) return undefined;
  return toE164(trimmed) || trimmed;
};

export const PHONE_ERROR = `Enter a valid mobile number, e.g. ${PHONE_EXAMPLE}`;
